import Api from '@/services/api';
import GetErgonomicWorkAnalysisImagesMapper from '@/core/mapper/ergonomic-work-analysis-get-images';
import type {
	GetErgonomicWorkAnalysisListRequestDTO,
	GetErgonomicWorkAnalysisListResponseDTO,
	CreateErgonomicWorkAnalysisRequestDTO,
	CreateErgonomicWorkAnalysisResponseDTO,
	DeleteErgonomicWorkAnalysisRequestDTO,
	DeleteErgonomicWorkAnalysisResponseDTO,
	GetErgonomicWorkAnalysisReportDownloadRequestDTO,
	GetErgonomicWorkAnalysisReportDownloadResponseDTO,
	CreateErgonomicWorkAnalysisUrlSignedRequestDTO,
	CreateErgonomicWorkAnalysisUrlSignedResponseDTO,
	GetErgonomicWorkAnalysisImagesRequestDTO,
	GetErgonomicWorkAnalysisImagesResponseDTO,
	UpdateErgonomicWorkAnalysisImageDescriptionRequestDTO,
	UpdateErgonomicWorkAnalysisImageDescriptionResponseDTO,
	DeleteErgonomicWorkAnalysisImageRequestDTO,
	DeleteErgonomicWorkAnalysisImageResponseDTO,
	UpdateErgonomicWorkAnalysisRequestDTO,
	UpdateErgonomicWorkAnalysisResponseDTO,
	GetErgonomicWorkAnalysisRequestDTO,
	GetErgonomicWorkAnalysisResponseDTO,
	GetSelectedCustomReportResultsRequestDTO,
	GetSelectedCustomReportResultsResponseDTO,
	GetAllActionPlansRequestDTO,
	GetAllActionPlansResponseDTO,
	GenerateDocumentErgonomicWorkAnalysisRequestDTO,
	GenerateDocumentErgonomicWorkAnalysisResponseDTO,
	ErgonomicWorkAnalysisImage
} from '@/core/dto/ergonomic-work-analisys';

export class ErgonomicWorkAnalysisService {
	private static instance: ErgonomicWorkAnalysisService;

	private constructor(public readonly basePath: string = '/ergonomic-work-analysis') {}

	public static getInstance(): ErgonomicWorkAnalysisService {
		if (!ErgonomicWorkAnalysisService.instance) {
			ErgonomicWorkAnalysisService.instance = new ErgonomicWorkAnalysisService();
		}
		return ErgonomicWorkAnalysisService.instance;
	}

	public async findAll(
		params: GetErgonomicWorkAnalysisListRequestDTO
	): Promise<GetErgonomicWorkAnalysisListResponseDTO> {
		const { data } = await Api.get<GetErgonomicWorkAnalysisListResponseDTO>(this.basePath, {
			params
		});
		return data;
	}

	public async create(
		params: CreateErgonomicWorkAnalysisRequestDTO
	): Promise<CreateErgonomicWorkAnalysisResponseDTO> {
		const { data } = await Api.post<CreateErgonomicWorkAnalysisResponseDTO>(this.basePath, params);
		return data;
	}

	public async delete(
		params: DeleteErgonomicWorkAnalysisRequestDTO
	): Promise<DeleteErgonomicWorkAnalysisResponseDTO> {
		const url = this.basePath + `/${params.id}`;
		const { data } = await Api.delete<DeleteErgonomicWorkAnalysisResponseDTO>(url, {
			params
		});
		return data;
	}

	public async downloadReport(
		params: GetErgonomicWorkAnalysisReportDownloadRequestDTO
	): Promise<GetErgonomicWorkAnalysisReportDownloadResponseDTO> {
		const url = this.basePath + `/${params.id}/download`;
		const { data } = await Api.get<GetErgonomicWorkAnalysisReportDownloadResponseDTO>(url, {
			params
		});
		return data;
	}

	public async update(
		params: UpdateErgonomicWorkAnalysisRequestDTO
	): Promise<UpdateErgonomicWorkAnalysisResponseDTO> {
		const url = this.basePath + `/${params.id}`;
		const { data } = await Api.put<UpdateErgonomicWorkAnalysisResponseDTO>(url, params);
		return data;
	}

	public async findOne(params: GetErgonomicWorkAnalysisRequestDTO): Promise<GetErgonomicWorkAnalysisResponseDTO> {
		const url = this.basePath + `/${params.id}`;
		const { data } = await Api.get<GetErgonomicWorkAnalysisResponseDTO>(url, { params });
		return data;
	}

	public async getSelectedCustomReportResults(
		params: GetSelectedCustomReportResultsRequestDTO
	): Promise<GetSelectedCustomReportResultsResponseDTO> {
		const url = this.basePath + `/${params.id}/custom-reports`;
		const { data } = await Api.get<GetSelectedCustomReportResultsResponseDTO>(url, { params });
		return data;
	}

	public async getImages(params: GetErgonomicWorkAnalysisImagesRequestDTO): Promise<ErgonomicWorkAnalysisImage[]> {
		const { id, ...query } = params;
		const url = this.basePath + `/${id}/images`;
		const { data } = await Api.get<GetErgonomicWorkAnalysisImagesResponseDTO[]>(url, { params: query });
		return new GetErgonomicWorkAnalysisImagesMapper(data).toDomain();
	}

	public async createUploadUrlSigned(
		params: CreateErgonomicWorkAnalysisUrlSignedRequestDTO
	): Promise<CreateErgonomicWorkAnalysisUrlSignedResponseDTO> {
		const url = this.basePath + `/upload-url-signed/${params.id}`;
		const { data } = await Api.post<CreateErgonomicWorkAnalysisUrlSignedResponseDTO>(url, params);
		return data;
	}

	public async updateImageDescription(
		params: UpdateErgonomicWorkAnalysisImageDescriptionRequestDTO
	): Promise<UpdateErgonomicWorkAnalysisImageDescriptionResponseDTO> {
		const url = this.basePath + `/${params.id}/images/${params.image_id}`;
		const { data } = await Api.put<UpdateErgonomicWorkAnalysisImageDescriptionResponseDTO>(url, params);
		return data;
	}

	public async deleteImage(
		params: DeleteErgonomicWorkAnalysisImageRequestDTO
	): Promise<DeleteErgonomicWorkAnalysisImageResponseDTO> {
		const url = this.basePath + `/${params.id}/images/${params.image_id}`;
		const { data } = await Api.delete<DeleteErgonomicWorkAnalysisImageResponseDTO>(url, { params });
		return data;
	}

	public async getAllActionPlans(params: GetAllActionPlansRequestDTO): Promise<GetAllActionPlansResponseDTO[]> {
		const url = this.basePath + `/${params.ergonomic_work_analysis_id}/action-plans`;
		const { data } = await Api.get<GetAllActionPlansResponseDTO[]>(url, { params });
		return data;
	}

	public async generateDocument(
		params: GenerateDocumentErgonomicWorkAnalysisRequestDTO
	): Promise<GenerateDocumentErgonomicWorkAnalysisResponseDTO> {
		const url = this.basePath + `/${params.id}/generate-document`;
		const { data } = await Api.post<GenerateDocumentErgonomicWorkAnalysisResponseDTO>(url, params);
		return data;
	}
}
